import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { vars } from '../../theme';
import { Close16 } from '@honeybook/icons';
import { dissolve, fromDirection } from '../../styles/animations';
import { mediumUp, smallUp } from '../../styles/breakpoints';
import { flexCenterAll, flexColumn, hideInPrint } from '../../styles/mixins';
import spacing from '../../styles/spacing';
import HBMediaElement from '../HBMediaElement/HBMediaElement';
import HBText, { TTextType } from '../HBText/HBText';

const LOGO_SIZE = 54;

export interface IProtipProps {
    className?: string;
    /** Cloudinary image URL rendered as a circular logo beside the text content. */
    iconUrl?: string;
    title?: string | (string | JSX.Element)[];
    text: string | (string | JSX.Element)[];
    /** Content rendered in a right-aligned action area below the text, typically a button or link. */
    action?: string | JSX.Element;
    /** Overrides the default `body14` text type for the main text content. */
    textType?: TTextType;
    /** Hides the close (dismiss) button in the top-right corner. */
    hideClose?: boolean;
    /** Automatically dismisses the pro tip after the specified number of milliseconds. */
    autoDismissMs?: number;
}

/** @deprecated Please use MemberToast from ToastStore: `type: ToastType.MemberToast` */
export default function HBProTip({
    className,
    iconUrl,
    title,
    text,
    action,
    textType,
    hideClose,
    autoDismissMs
}: IProtipProps) {
    const [animateIn, setAnimateIn] = useState(true);
    const [showProtip, setShowProtip] = useState(true);
    const timeoutRef = useRef(null);
    const [isDoneShowing, setIsDoneShowing] = useState(false);

    useEffect(() => {
        if (!isDoneShowing && autoDismissMs) {
            timeoutRef.current = setTimeout(() => {
                setIsDoneShowing(true);
                setAnimateIn(false);
            }, autoDismissMs);
            return () => {
                clearTimeout(timeoutRef.current);
            };
        }
        return null;
    }, [isDoneShowing, autoDismissMs]);

    const handleClose = () => {
        setAnimateIn(false);
    };

    const handleAnimationEnd = () => {
        if (!animateIn) {
            setShowProtip(false);
        }
    };

    if (!showProtip) {
        return null;
    }

    return (
        <HBProTipContainer
            className={className}
            animateIn={animateIn}
            onAnimationEnd={handleAnimationEnd}
        >
            {!hideClose && (
                <CloseIcon
                    color={vars.colors.text.secondary}
                    onClick={handleClose}
                />
            )}

            <LogoAndTextContainer>
                {iconUrl && (
                    <CompanyLogo>
                        <HBMediaElement
                            width={LOGO_SIZE}
                            height={LOGO_SIZE}
                            path={iconUrl}
                            crop="fit"
                        />
                    </CompanyLogo>
                )}

                <TextContainer>
                    {title && <Title>{title}</Title>}

                    <Text type={textType || 'body14'}>{text}</Text>
                </TextContainer>
            </LogoAndTextContainer>

            {action && <ActionContainer>{action}</ActionContainer>}
        </HBProTipContainer>
    );
}

// -------------------------------- Styles -------------------------------- //
export const HBProTipContainer = styled.div<{
    animateIn: boolean;
}>`
    max-width: 100%;
    // Aligns margins with the flow margins on small screens
    margin: 0 4px;

    z-index: 3;
    padding: ${spacing[20]};
    background-color: ${vars.colors.surface.container};
    border-radius: ${vars.radius.md};
    box-shadow: ${vars.shadow['40']};
    position: sticky; // Prevents Protip from hiding content on bottom in mweb
    bottom: ${spacing[20]};

    ${smallUp} {
        max-width: 420px;
        margin: 0 auto;
    }

    ${mediumUp} {
        position: fixed;
        right: ${spacing[20]};
        min-width: 350px;
    }

    ${({ animateIn }) =>
        animateIn &&
        css`
            animation: ${fromDirection('down')} 1s;
        `}

    ${({ animateIn }) =>
        !animateIn &&
        css`
            animation: ${dissolve('up')} 0.7s;
        `};

    ${hideInPrint}
`;

export const CloseIcon = styled(Close16)`
    position: absolute;
    top: ${spacing[12]};
    right: ${spacing[12]};
`;

export const CompanyLogo = styled.div`
    min-width: ${LOGO_SIZE}px;
    height: ${LOGO_SIZE}px;
    ${flexCenterAll};
    border-radius: ${vars.radius.full};
    border: 1px solid ${vars.colors.stroke.tertiary};
    opacity: 0.7;
    margin-right: ${spacing[12]};
    overflow: hidden;
`;

export const LogoAndTextContainer = styled.div`
    display: flex;
    align-items: top;
`;

export const TextContainer = styled.div`
    ${flexColumn}
    margin-top: ${spacing[4]};
    margin-right: ${spacing[20]};
`;

export const Title = styled(HBText).attrs({ type: 'title14' })`
    display: block;
    margin-bottom: ${spacing[4]};
`;

export const Text = styled(HBText)`
    display: block;
    white-space: pre-line;
`;

export const ActionContainer = styled.div`
    margin-top: ${spacing[12]};
    display: flex;
    justify-content: flex-end;
    button {
        margin-bottom: 0;
    }
`;

export const Action = styled(HBText)``;
